// /admin/exchanges — search list + per-row admin force-transition modal.
//
// Wave 4J adds the "Force transition" affordance: an admin can override the
// canonical state machine (within a curated reachability matrix) when the SM
// has gotten stuck or a row needs a manual repair. The actual surface is a
// Mantine Modal that loads the row's reachable target states from the worker
// and POSTs the choice + a required reason to
// `/api/admin/exchanges/:id/force-transition`.

import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { AdminShell } from '../../components/admin-shell/AdminShell';
import { adminGet, adminPost } from './_api';

type ExchangeRow = {
  id: string;
  state: string;
  artist_user_id: string;
  artist_name: string;
  venue_id: string;
  venue_name: string;
  venue_slug: string | null;
  start_date: string;
  end_date: string;
  party_size: number;
  updated_at: string;
};

type ExchangesList = {
  items: ExchangeRow[];
  total: number;
};

type ReachableStates = {
  from: string;
  reachable: string[];
};

type ForceTransitionResponse = {
  ok: boolean;
  from: string;
  to: string;
  exchange: { id: string; state: string; updated_at: string } | null;
};

const PAGE_SIZE = 25;
const STATES = [
  'drafted',
  'submitted',
  'confirmed',
  'completed',
  'cancelled',
  'declined',
  'disputed',
  'closed',
];

export const Route = createFileRoute('/admin/exchanges')({
  component: AdminExchangesPage,
});

function AdminExchangesPage() {
  const [state, setState] = useState<string | null>(null);
  const [partyUserId, setPartyUserId] = useState('');
  const [page, setPage] = useState(1);
  const [forceFor, setForceFor] = useState<ExchangeRow | null>(null);
  const offset = (page - 1) * PAGE_SIZE;

  const params = new URLSearchParams();
  if (state) params.set('state', state);
  if (partyUserId.trim()) params.set('party_user_id', partyUserId.trim());
  params.set('limit', String(PAGE_SIZE));
  params.set('offset', String(offset));

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'exchanges', state, partyUserId, page],
    queryFn: () => adminGet<ExchangesList>(`/api/admin/exchanges?${params.toString()}`),
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <AdminShell>
      <Stack gap="md">
        <Title order={2}>Exchanges</Title>

        <Group gap="sm" wrap="wrap">
          <Select
            placeholder="State"
            data={STATES}
            clearable
            value={state}
            onChange={(v) => {
              setState(v);
              setPage(1);
            }}
            style={{ minWidth: 180 }}
          />
          <TextInput
            placeholder="Party user id"
            value={partyUserId}
            onChange={(e) => {
              setPartyUserId(e.currentTarget.value);
              setPage(1);
            }}
            style={{ minWidth: 240 }}
          />
        </Group>

        {isLoading ? (
          <Group>
            <Loader size="sm" />
            <Text c="dimmed">Loading…</Text>
          </Group>
        ) : (
          <>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Venue</Table.Th>
                  <Table.Th>Artist</Table.Th>
                  <Table.Th>Dates</Table.Th>
                  <Table.Th>Party</Table.Th>
                  <Table.Th>State</Table.Th>
                  <Table.Th>Updated</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(data?.items ?? []).map((e) => (
                  <Table.Tr key={e.id}>
                    <Table.Td>{e.venue_name}</Table.Td>
                    <Table.Td>{e.artist_name}</Table.Td>
                    <Table.Td>
                      {e.start_date} → {e.end_date}
                    </Table.Td>
                    <Table.Td>{e.party_size}</Table.Td>
                    <Table.Td>
                      <Badge variant="light">{e.state}</Badge>
                    </Table.Td>
                    <Table.Td>{relative(e.updated_at)}</Table.Td>
                    <Table.Td>
                      <Button
                        size="xs"
                        variant="subtle"
                        color="yellow"
                        onClick={() => setForceFor(e)}
                      >
                        Force transition
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            {(data?.items.length ?? 0) === 0 ? <Text c="dimmed">No exchanges.</Text> : null}
            <Group justify="space-between">
              <Text c="dimmed" size="sm">
                {data?.total ?? 0} total
              </Text>
              <Pagination value={page} onChange={setPage} total={totalPages} />
            </Group>
          </>
        )}
      </Stack>

      <ForceTransitionModal
        exchange={forceFor}
        onClose={() => setForceFor(null)}
      />
    </AdminShell>
  );
}

// ---------------------------------------------------------------------------
// Force-transition modal — opens with a row, fetches its reachable states,
// requires a reason, POSTs the override, and invalidates the list query so
// the new state shows up after submit.
// ---------------------------------------------------------------------------

function ForceTransitionModal({
  exchange,
  onClose,
}: {
  exchange: ExchangeRow | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [target, setTarget] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reachableQuery = useQuery({
    queryKey: ['admin', 'exchange-reachable', exchange?.id],
    queryFn: () =>
      exchange
        ? adminGet<ReachableStates>(
            `/api/admin/exchanges/${encodeURIComponent(exchange.id)}/reachable-states`,
          )
        : Promise.reject(new Error('no exchange')),
    enabled: exchange !== null,
  });

  const mutation = useMutation({
    mutationFn: async (input: { exchangeId: string; targetState: string; reason: string }) =>
      adminPost<ForceTransitionResponse>(
        `/api/admin/exchanges/${encodeURIComponent(input.exchangeId)}/force-transition`,
        { target_state: input.targetState, reason: input.reason },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'exchanges'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'audit'] });
      handleClose();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Could not force the transition.');
    },
  });

  const handleClose = () => {
    setTarget(null);
    setReason('');
    setError(null);
    onClose();
  };

  const reachable = reachableQuery.data?.reachable ?? [];
  const reasonValid = reason.trim().length >= 8;
  const canSubmit =
    exchange !== null && target !== null && reasonValid && !mutation.isPending;

  return (
    <Modal
      opened={exchange !== null}
      onClose={handleClose}
      title={
        exchange !== null
          ? `Force transition · ${exchange.venue_name} · ${exchange.artist_name}`
          : 'Force transition'
      }
      centered
      size="md"
    >
      {exchange === null ? null : (
        <Stack gap="sm">
          <Alert color="yellow" variant="light">
            This bypasses the state machine. Use it only when the canonical flow has gotten stuck
            or a row genuinely needs a manual repair. The override is recorded in the audit log
            with your user id and the reason below.
          </Alert>

          <Group gap="xs">
            <Text size="sm" c="dimmed">
              Current state:
            </Text>
            <Badge variant="light">{exchange.state}</Badge>
          </Group>

          {reachableQuery.isLoading ? (
            <Group>
              <Loader size="xs" />
              <Text size="sm" c="dimmed">
                Loading reachable states…
              </Text>
            </Group>
          ) : reachable.length === 0 ? (
            <Text size="sm" c="dimmed">
              This state is terminal. No force transition is allowed from here.
            </Text>
          ) : (
            <Select
              label="Target state"
              placeholder="Pick one"
              data={reachable}
              value={target}
              onChange={(v) => {
                setTarget(v);
                setError(null);
              }}
            />
          )}

          <Textarea
            label="Reason"
            description="At least 8 characters. Both parties see a system message in the thread; this reason is recorded in the audit log."
            placeholder="What needed fixing and why force was the right call."
            autosize
            minRows={3}
            maxLength={2_000}
            value={reason}
            onChange={(e) => {
              setReason(e.currentTarget.value);
              setError(null);
            }}
          />

          {error !== null ? (
            <Alert color="red" variant="light">
              {error}
            </Alert>
          ) : null}

          <Group justify="flex-end">
            <Button variant="default" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              color="yellow"
              loading={mutation.isPending}
              disabled={!canSubmit}
              onClick={() => {
                if (target === null) return;
                mutation.mutate({
                  exchangeId: exchange.id,
                  targetState: target,
                  reason: reason.trim(),
                });
              }}
            >
              Apply override
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

function relative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const hours = Math.floor(ms / (60 * 60 * 1000));
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
